import { Link, Outlet, useLocation, useNavigate } from "react-router-dom"
import Besakshampf from '../../../assets/HosakshamHorizontal.png'
import BesakshamOnlyLogo from '../../../assets/HosakshamOnlyLogo.png'
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { useEffect, useState, useCallback } from "react";
import { auth, db, FLAVOR } from "../../../Config/firebase.ts";
import { checkUserInFirestore, setAuthenticated, selectEffectiveUID, selectActiveRole, selectAvailableRoles } from "../../../state/UserStatesV2/userSliceV2.ts";
import { signOut } from "firebase/auth"
import ModalManager from "../../Common/Modals/ModalManager.tsx";
import { actions as actions_LiveSub } from "../../../state/LiveSubscriptionSlice/LiveSubscriptionSlice.ts";
import { genericActions as actions_Sub } from "../../../state/subscriptionTableState/subscriptionTableSlice.ts"
import { actions as actions_orders } from "../../../state/OrderState/OrderStatsSlice.ts";
import { collection, getDocs, query, where, doc, getDoc, onSnapshot, DocumentData } from "firebase/firestore";
import { SECRETS_ENV } from "../../../Config/firebase.ts";
import NotAuthorizedPage from "./NotAuthorizedPage.tsx";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallbackPage } from "../Pages/FallbackPages/errorFallback.tsx";
import NotificationsPanel from '../Pages/Notifications/Notification';
import { useBackend } from "../../../hooks/backend/useBackend.ts";
import { GenericActions } from "../../../state/GenericSlice.ts";
import NavHeader from '../Components/NavHeader';
import React from "react";
import { getThemeColors } from "../../../Config/theme";
import { getCustomBrandingSubdomainFromHost } from "../../../utils/customDomainSubdomain.ts";

export default function Layout() {
    const Dispatch = useAppDispatch();
    const navigate = useNavigate();
    const userSliceV2 = useAppSelector((state: RootState) => state.userSliceV2);
    const authState: boolean | undefined = userSliceV2.data?.isAuthenticated;
    const [currPath, setCurrPath] = useState<string>("");
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const location = useLocation();
    const activeRole = useAppSelector(selectActiveRole);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const drawerTimeoutRef = React.useRef<NodeJS.Timeout>();
    const availableRoles = useAppSelector(selectAvailableRoles);
    const hasRoles = (availableRoles && availableRoles.length > 0) || false
    const role = activeRole?.role;
    const theme = getThemeColors(role);
    const isOwner = !role;


    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (drawerTimeoutRef.current) {
                clearTimeout(drawerTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const path: string[] = location.pathname.split("/");
        if (path[1]) {
            setCurrPath(path[1]);
        } else {
            setCurrPath("");
        }
    }, [location]);

    useEffect(() => {
        if (typeof authState !== 'undefined') {
            if (authState === false) {
                navigate("/login");
            }
        }
    }, [authState, navigate, userSliceV2]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async user => {

            if (!user) {
                console.log("User is not logged in");
                await signOut(auth);
                Dispatch(setAuthenticated(false));
                navigate("/login");
                return;
            }

            // Check if user exists in ServiceProviders collection
            const serviceProviderRef = doc(db, 'ServiceProviders', user.uid);
            const serviceProviderDoc = await getDoc(serviceProviderRef);

            if (!serviceProviderDoc.exists()) {
                // User doesn't exist in ServiceProviders collection
                await signOut(auth);
                Dispatch(setAuthenticated(false));
                navigate("/login");
                return;
            }

            Dispatch(checkUserInFirestore(user.uid));
            Dispatch(setAuthenticated(true));
        });

        return () => unsubscribe();
    }, [Dispatch]);

    // Logout
    const handleLogout = () => {
        signOut(auth).then(() => {
            // Sign-out successful.
            window.location.href = "/login"
        }).catch(() => {
            // An error happened.
        });
    }

    // ---------------------- Collection Listeners[Common] in fireStore
    const ServiceProviderID = useAppSelector(selectEffectiveUID);

    // Get the full path for the active service provider
    const serviceProviderPath = useAppSelector((state) => {
        const activeRole = state.userSliceV2.data?.activeRole;
        if (activeRole?.serviceProviderReference) {
            return activeRole.serviceProviderReference;
        }
        return `ServiceProviders/${ServiceProviderID}`;
    });

    // ---- Accessing Service Provider Id and Loading the subscription Collection
    const { loadCollection: loadCollectionSub } = useBackend(`ServiceProviders/${ServiceProviderID}/Subscriptions`)
    const { loadCollection: loadCollectionLiveSub } = useBackend(`ServiceProviders/${ServiceProviderID}/LiveSubscriptions`)

    useEffect(() => {


        if (ServiceProviderID) {
            loadCollectionSub(actions_Sub as GenericActions<DocumentData>, {
                sort: {
                    attribute: 'CreatedAt',
                    order: 'desc'
                }
            }, activeRole);
            loadCollectionLiveSub(actions_LiveSub as GenericActions<DocumentData>, {
                sort: {
                    attribute: 'startDate',
                    order: 'desc'
                }
            }, activeRole);
        }
    }, [loadCollectionSub, loadCollectionLiveSub, ServiceProviderID])

    // --------Accessing Orders
    const { loadCollection } = useBackend(`${serviceProviderPath}/Orders`);

    useEffect(() => {
        if (ServiceProviderID) {
            loadCollection(actions_orders as GenericActions<DocumentData>, {
                sort: {
                    attribute: 'orderTime',
                    order: 'desc'
                }
            }, activeRole);
        }
    }, [loadCollection, ServiceProviderID]);

    // Subdomain auth Check
    // ---------------- Subdomain config
    const [subDomain, setSubDomain] = useState<string | null>(null);
    // Error for subdomain authorised users
    const [subDomainAlowanceError, setSubDomainAlowanceError] = useState<boolean>(false);

    function onClickProfilePic() {
        navigate("/settings")
    }

    useEffect(() => {
        const host = window.location.host;
        const potentialSubDomain = getCustomBrandingSubdomainFromHost(host);

        if (potentialSubDomain?.includes("localhost")) {
            return;
        }

        if (potentialSubDomain && !SECRETS_ENV.EXCLUDED_DOMAINS.includes(potentialSubDomain)) {
            setSubDomain(potentialSubDomain);
        }
    }, []);

    // Memoize the retreiveDocsForSubdomain function to prevent unnecessary re-executions
    const retreiveDocsForSubdomain = useCallback(async () => {
        if (!subDomain || !userSliceV2?.data?.phone_number) return;

        try {
            const subdomainQuery = query(collection(db, 'CustomBranding'), where('subdomain', '==', `${subDomain}.${SECRETS_ENV.DOMAIN}`));
            const querySnapshot = await getDocs(subdomainQuery);

            if (!querySnapshot.empty && userSliceV2?.data?.phone_number) {
                const firstDoc = querySnapshot.docs[0];
                const authorizedNumbers = firstDoc.data().authorizedNumbers;

                if (authorizedNumbers && authorizedNumbers.includes(userSliceV2?.data?.phone_number)) {
                    setSubDomainAlowanceError(false);
                } else {
                    setSubDomainAlowanceError(true);
                }
            }
        } catch (error) {
            console.error("Error checking subdomain authorization:", error);
            setSubDomainAlowanceError(true);
        }
    }, [subDomain, userSliceV2?.data?.phone_number]);

    useEffect(() => {
        if (subDomain) {
            retreiveDocsForSubdomain();
        }
    }, [subDomain, retreiveDocsForSubdomain]);

    // Show loading state or render the appropriate content based on authorization


    // Add this effect to check for unread notifications
    useEffect(() => {
        const checkUnreadNotifications = () => {
            if (ServiceProviderID) {
                // ServiceProviderID is already the correct ID, no need to manipulate the path
                const pendingRequestsRef = collection(db, "ServiceProviders", ServiceProviderID, "pendingRequests");
                const q = query(pendingRequestsRef);

                // Use onSnapshot for real-time updates
                const unsubscribe = onSnapshot(q, (querySnapshot) => {
                    const unreadNotifications = querySnapshot.docs.filter(doc => !doc.data().ReadRequest);
                    setUnreadCount(unreadNotifications.length);
                });

                return () => unsubscribe(); // Cleanup subscription on unmount
            }
        };

        checkUnreadNotifications();
    }, [ServiceProviderID]);



    if (subDomainAlowanceError) {
        return (
            <div className="w-full h-full p-10 flex ">
                <NotAuthorizedPage subDomain={subDomain} />
            </div>
        );
    }

    const handleNotificationClick = () => {
        setIsNotificationOpen(!isNotificationOpen);
    };

    const handleDrawerOpen = () => {
        if (drawerTimeoutRef.current) {
            clearTimeout(drawerTimeoutRef.current);
        }
        setIsDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        if (drawerTimeoutRef.current) {
            clearTimeout(drawerTimeoutRef.current);
        }
        drawerTimeoutRef.current = setTimeout(() => {
            setIsDrawerOpen(false);
        }, 300); // 300ms delay before closing
    };

    const handleMobileMenuToggle = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const handleMobileMenuClose = () => {
        setIsMobileMenuOpen(false);
    };

    return (<div className={"bg-cc_bg_app h-[100dvh]"}>
        <ModalManager />
        <NotificationsPanel
            open={isNotificationOpen}
            onClose={() => setIsNotificationOpen(false)}
        />
        <header
            className={`fixed top-0 inset-x-0 flex flex-wrap sm:justify-end sm:flex-nowrap z-[48] w-full text-sm lg:ps-64 shadow-md
            ${theme.background} ${hasRoles ? 'py' : 'py-2.5'}`}>
            <nav className="flex basis-full items-center justify-end w-full mx-auto px-4 sm:px-6 border-gray-200" aria-label="Global">

                <div className="flex items-center justify-end gap-3 sm:gap-4">
                    <NavHeader />
                    <div className="relative">
                        <button
                            type="button"
                            className="w-[1.75rem] h-[1.75rem] inline-flex justify-center items-center gap-x-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
                            onClick={handleNotificationClick}
                        >
                            <svg
                                className="flex-shrink-0 size-5"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                                <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                            </svg>
                            {unreadCount > 0 && (
                                <span className="absolute top-[-3px] right-[-1px] inline-flex items-center justify-center w-4 h-4 bg-red-500 text-white text-xs rounded-full">
                                    {unreadCount}
                                </span>
                            )}
                        </button>
                    </div>
                    <div className="hs-dropdown [--placement:bottom-right] relative inline-flex">
                        <button onClick={onClickProfilePic} id="hs-dropdown-with-header" type="button"
                            className="w-[2.375rem] h-[2.375rem] inline-flex justify-center items-center gap-x-2 text-sm font-semibold rounded-full border border-transparent text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none">
                            <img className="inline-block size-[38px] rounded-full ring-2 ring-white"
                                src={userSliceV2.data?.photo_url || "https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FPF_Dummy.jpg?alt=media&token=ffa2bd9c-9817-46c9-9a2f-8d2e04f6ec37"} alt="Image Description" />
                        </button>

                    </div>
                    {/* Hamburger Menu Button - Visible on mobile only (hidden on desktop where sidebar is visible) */}
                    <button 
                        type="button"
                        onClick={handleMobileMenuToggle}
                        className="lg:hidden w-[2rem] h-[2rem] sm:w-[2.25rem] sm:h-[2.25rem] inline-flex justify-center items-center rounded-lg border border-gray-300 text-gray-700 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-colors"
                        aria-label="Menu"
                    >
                        <svg className="flex-shrink-0 size-5 sm:size-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                            strokeLinejoin="round">
                            {isMobileMenuOpen ? (
                                <path d="M18 6L6 18M6 6l12 12" />
                            ) : (
                                <path d="M3 12h18M3 6h18M3 18h18" />
                            )}
                        </svg>
                    </button>
                </div>
            </nav>
        </header>

        {/* Right Side Menu Overlay - Visible on all screens */}
        {isMobileMenuOpen && (
            <div 
                className="fixed inset-0 bg-black bg-opacity-50 z-[59]"
                onClick={handleMobileMenuClose}
            />
        )}

        {/* Right Side Menu - Visible on all screens */}
        <div 
            className={`fixed inset-y-0 right-0 z-[60] w-64 sm:w-72 ${theme.background} border-l ${theme.border} shadow-2xl transform transition-transform duration-300 ease-in-out ${
                isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
        >
            <div className="flex flex-col h-full">
                <div className={`px-5 pt-4 pb-3 border-b ${theme.border}`}>
                    <div className="flex items-center justify-between">
                        <img src={Besakshampf} alt="Hosaksham" className="h-12" />
                        <button
                            onClick={handleMobileMenuClose}
                            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    {activeRole?.role && (
                        <div className={`mt-3 text-xs font-medium px-2 py-2 rounded text-center ${theme.bar} text-white`}>
                            {activeRole.role}
                        </div>
                    )}
                </div>
                <nav className={`flex-1 overflow-y-auto p-4 ${theme.text}`}>
                    <ul className="space-y-1">
                        <li>
                            <Link 
                                to="/mydashboard"
                                onClick={handleMobileMenuClose}
                                className={`flex items-center gap-x-3 py-2.5 px-3 text-sm rounded-lg ${
                                    location.pathname === "/mydashboard" ? theme.active : theme.hover
                                }`}
                            >
                                <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                    <polyline points="9 22 9 12 15 12 15 22" />
                                </svg>
                                My Dashboard
                            </Link>
                        </li>
                        <li>
                            <Link 
                                to="/mysubscriptions"
                                onClick={handleMobileMenuClose}
                                className={`flex items-center gap-x-3 py-2.5 px-3 text-sm rounded-lg ${
                                    currPath === "mysubscriptions" ? theme.active : theme.hover
                                }`}
                            >
                                <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                                    <path d="M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />
                                    <path d="M8 10h8" />
                                    <path d="M8 14h8" />
                                    <path d="M8 18h8" />
                                </svg>
                                Subscriptions
                            </Link>
                        </li>
                        <li>
                            <Link 
                                to="/events-offers"
                                onClick={handleMobileMenuClose}
                                className={`flex items-center gap-x-3 py-2.5 px-3 text-sm rounded-lg ${
                                    currPath === "events-offers" ? theme.active : theme.hover
                                }`}
                            >
                                <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                                Events-Offers
                            </Link>
                        </li>
                        {FLAVOR === 'DEVELOPMENT' && (
                            <li>
                                <Link 
                                    to="/storefront"
                                    onClick={handleMobileMenuClose}
                                    className={`flex items-center gap-x-3 py-2.5 px-3 text-sm rounded-lg ${
                                        currPath === "storefront" ? theme.active : theme.hover
                                    }`}
                                >
                                    <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
                                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                        <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
                                        <path d="M2 7h20" />
                                    </svg>
                                    Storefront
                                </Link>
                            </li>
                        )}
                        <li>
                            <Link 
                                to="/exports"
                                onClick={handleMobileMenuClose}
                                className={`flex items-center gap-x-3 py-2.5 px-3 text-sm rounded-lg ${
                                    currPath === "exports" ? theme.active : theme.hover
                                }`}
                            >
                                <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Export Data
                            </Link>
                        </li>
                        <li>
                            <Link 
                                to="/bulkrequests"
                                onClick={handleMobileMenuClose}
                                className={`flex items-center gap-x-3 py-2.5 px-3 text-sm rounded-lg ${
                                    currPath === "bulkrequests" ? theme.active : theme.hover
                                }`}
                            >
                                <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22V12" />
                                    <path d="m17 7-5-5-5 5" />
                                    <rect x="3" y="15" width="4" height="6" rx="1" />
                                    <rect x="10" y="12" width="4" height="9" rx="1" />
                                    <rect x="17" y="9" width="4" height="12" rx="1" />
                                </svg>
                                Bulk Requests
                            </Link>
                        </li>
                        <li>
                            <Link 
                                to="/ledger"
                                onClick={handleMobileMenuClose}
                                className={`flex items-center gap-x-3 py-2.5 px-3 text-sm rounded-lg ${
                                    currPath === "ledger" ? theme.active : theme.hover
                                }`}
                            >
                                <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                                    <path d="M8 7h8" />
                                    <path d="M8 11h8" />
                                    <path d="M8 15h4" />
                                </svg>
                                Ledger
                            </Link>
                        </li>
                        <li>
                            <Link 
                                to="/inflow"
                                onClick={handleMobileMenuClose}
                                className={`flex items-center gap-x-3 py-2.5 px-3 text-sm rounded-lg ${
                                    currPath === "inflow" ? theme.active : theme.hover
                                }`}
                            >
                                <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                                Inflow
                            </Link>
                        </li>
                        <li>
                            <Link 
                                to="/myworld"
                                onClick={handleMobileMenuClose}
                                className={`flex items-center gap-x-3 py-2.5 px-3 text-sm rounded-lg ${
                                    currPath === "myworld" ? theme.active : theme.hover
                                }`}
                            >
                                <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M8 2v4" />
                                    <path d="M16 2v4" />
                                    <path d="M21 6H3a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1z" />
                                    <path d="M3 10h18" />
                        </svg>
                                My World
                            </Link>
                    </li>
                        <li>
                            <Link 
                                to="/settings"
                                onClick={handleMobileMenuClose}
                                className={`flex items-center gap-x-3 py-2.5 px-3 text-sm rounded-lg ${
                                    currPath === "settings" || currPath === "profile" ? theme.active : theme.hover
                                }`}
                            >
                                <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="3" />
                                    <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24" />
                                </svg>
                                Settings
                            </Link>
                    </li>
                        {isOwner && (
                            <li className="pt-4 border-t border-gray-200">
                                <Link 
                                    to="/superadmin"
                                    onClick={handleMobileMenuClose}
                                    className="flex items-center gap-x-3 py-2.5 px-3 text-sm rounded-lg bg-gradient-to-r from-violet-500 to-indigo-500 text-white hover:from-violet-600 hover:to-indigo-600"
                                >
                                    <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                    SuperAdmin Dashboard
                                </Link>
                            </li>
                        )}
                        <li className={`pt-4 border-t border-gray-200 ${isOwner && currPath !== "superadmin" ? '' : 'mt-0'}`}>
                            <button
                                onClick={() => {
                                    handleMobileMenuClose();
                                    handleLogout();
                                }}
                                className="flex items-center gap-x-3 py-2.5 px-3 text-sm rounded-lg text-red-600 hover:bg-red-50 w-full text-left"
                            >
                                <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                    <polyline points="16 17 21 12 16 7" />
                                    <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                                Logout
                </button>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>

        {/*Sidebar - Desktop Only*/}

        <div id="application-sidebar"
            onMouseEnter={handleDrawerOpen}
            onMouseLeave={handleDrawerClose}
            className={`hs-overlay
                hs-overlay-open:translate-x-0 transition-all duration-300 transform fixed inset-y-0 start-0 z-[60]
                ${isDrawerOpen ? "w-[220px]" : "w-[80px] hover:w-[220px]"}
                ${theme.background} 
                border-e ${theme.border} 
                hidden lg:block lg:translate-x-0 lg:end-auto lg:bottom-0 shadow-lg`}
        >
            <div className={`px-5 pt-2.5 h-16 ${hasRoles ? theme.border : ''}`}>
                <Link
                    className={`transition-opacity duration-300 ${isDrawerOpen ? "opacity-100 " : "opacity-0 hidden"}`}
                    to={"/mydashboard"}
                    aria-label="Preline"
                >
                    <img src={Besakshampf} alt={"beSaksham"} className="h-16" />
                </Link>

                <Link
                    className={`transition-opacity duration-300 mt-2.5 ${!isDrawerOpen ? "opacity-100" : "opacity-0 hidden"}`}
                    to={"/mydashboard"}
                    aria-label="Preline"
                >

                    <img src={BesakshamOnlyLogo} alt={"beSaksham"} className="h-8 mt-2.5" />
                </Link>

                {/* Persistent role indicator */}

            </div>


            {activeRole?.role && (
                <div className={`mt-2 text-xs font-medium px-2 py-2.5 rounded text-center transition-all duration-300
                        ${isDrawerOpen ? 'w-auto opacity-100' : 'w-[40px] text-[10px] opacity-0'}
                        ${theme.bar} text-white`}>
                    {isDrawerOpen ? activeRole?.role : ''}
                </div>
            )}

            <nav className={`hs-accordion-group p-6 w-full h-full flex flex-col flex-wrap overflow-hidden transition-all duration-300
                ${theme.text}`}
                data-hs-accordion-always-open>
                <ul className="space-y-1.5">
                    <li>
                        <Link to={"/mydashboard"}
                            style={{ width: isDrawerOpen ? 'auto' : '40px', overflowX: "hidden" }}
                            className={`transition-all duration-500 ease-in-out flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg 
                                ${location.pathname === "/mydashboard" ? theme.active : theme.text} 
                                ${theme.hover}`}>
                            <svg className="flex-shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24"
                                height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                <polyline points="9 22 9 12 15 12 15 22" />
                            </svg>
                            <span className={`${isDrawerOpen ? 'opacity-100 max-width-[200px] ml-2' : 'opacity-0 max-width-0 ml-0 hidden'}`}>
                                My Dashboard
                            </span>
                        </Link>
                    </li>
                    <li>
                        <Link to={"/mysubscriptions"}
                            style={{ width: isDrawerOpen ? 'auto' : '40px', overflowX: "hidden" }}
                            className={`transition-all duration-500 ease-in-out flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg 
                                ${currPath === "mysubscriptions" ? theme.active : theme.text} 
                                ${theme.hover}`}>
                            <svg className="flex-shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24"
                                height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                                <path d="M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />
                                <path d="M8 10h8" />
                                <path d="M8 14h8" />
                                <path d="M8 18h8" />
                            </svg>
                            <span className={`${isDrawerOpen ? 'opacity-100 max-width-[200px] ml-2' : 'opacity-0 max-width-0 ml-0 hidden'}`}>
                                Subscriptions
                            </span>
                        </Link>
                    </li>
                    <li>
                        <Link to={"/events-offers"}
                            style={{ width: isDrawerOpen ? 'auto' : '40px', overflowX: "hidden" }}
                            className={`transition-all duration-500 ease-in-out flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg 
                                ${currPath === "events-offers" ? theme.active : theme.text} 
                                ${theme.hover}`}>
                            <svg
                                className="flex-shrink-0 size-4"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                                <path d="M8 14h.01" />
                                <path d="M12 14h.01" />
                                <path d="M16 14h.01" />
                                <path d="M8 18h.01" />
                                <path d="M12 18h.01" />
                                <path d="M16 18h.01" />
                            </svg>

                            <span
                                className={` ${isDrawerOpen ? 'opacity-100 max-width-[200px] ml-2 ' : 'opacity-0 max-width-0 ml-0 hidden'}`}>Events-Offers</span>
                        </Link>
                    </li>

                    {FLAVOR === 'DEVELOPMENT' && <li>
                        <Link to={"/storefront"}
                            style={{ width: isDrawerOpen ? 'auto' : '40px', overflowX: "hidden" }}
                            className={`transition-all duration-500 ease-in-out flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg 
                                ${currPath === "storefront" ? theme.active : theme.text} 
                                ${theme.hover}`}>
                            <svg
                                className="flex-shrink-0 size-4"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
                                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
                                <path d="M2 7h20" />
                            </svg>
                            <span
                                className={` ${isDrawerOpen ? 'opacity-100 max-width-[200px] ml-2 ' : 'opacity-0 max-width-0 ml-0 hidden'}`}>
                                Storefront
                            </span>
                        </Link>
                    </li>}
                    <li>
                        <Link to={"/exports"}
                            style={{ width: isDrawerOpen ? 'auto' : '40px', overflowX: "hidden" }}
                            className={`transition-all duration-500 ease-in-out flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg 
                                ${currPath === "exports" ? theme.active : theme.text} 
                                ${theme.hover}`}
                        >
                            <svg className="flex-shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            <span className={`${isDrawerOpen ? 'opacity-100 max-width-[200px] ml-2' : 'opacity-0 max-width-0 ml-0 hidden'}`}>
                                Export Data
                            </span>
                        </Link>
                    </li>

                    <li>
                        <Link to={"/bulkrequests"}
                            style={{ width: isDrawerOpen ? 'auto' : '40px', overflowX: "hidden" }}
                            className={`transition-all duration-500 ease-in-out flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg 
                                ${currPath === "bulkrequests" ? theme.active : theme.text} 
                                ${theme.hover}`}
                        >
                            <svg className="flex-shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22V12" />
                                <path d="m17 7-5-5-5 5" />
                                <rect x="3" y="15" width="4" height="6" rx="1" />
                                <rect x="10" y="12" width="4" height="9" rx="1" />
                                <rect x="17" y="9" width="4" height="12" rx="1" />
                            </svg>
                            <span className={`${isDrawerOpen ? 'opacity-100 max-width-[200px] ml-2' : 'opacity-0 max-width-0 ml-0 hidden'}`}>
                                Bulk Requests
                            </span>
                        </Link>
                    </li>

                    <li>
                        <Link to={"/ledger"}
                            style={{ width: isDrawerOpen ? 'auto' : '40px', overflowX: "hidden" }}
                            className={`transition-all duration-500 ease-in-out flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg 
                                ${currPath === "ledger" ? theme.active : theme.text} 
                                ${theme.hover}`}
                        >
                            <svg
                                className="flex-shrink-0 size-4"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M3 3v18h18" />
                                <path d="m19 9-5 5-4-4-3 3" />
                            </svg>
                            <span className={`${isDrawerOpen ? 'opacity-100 max-width-[200px] ml-2' : 'opacity-0 max-width-0 ml-0 hidden'}`}>
                                Ledger
                            </span>
                        </Link>
                    </li>

                    <li>
                        <Link to={"/inflow"}
                            style={{ width: isDrawerOpen ? 'auto' : '40px', overflowX: "hidden" }}
                            className={`transition-all duration-500 ease-in-out flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg 
                                ${currPath === "inflow" ? theme.active : theme.text} 
                                ${theme.hover}`}
                        >
                            <svg
                                className="flex-shrink-0 size-4"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            <span className={`${isDrawerOpen ? 'opacity-100 max-width-[200px] ml-2' : 'opacity-0 max-width-0 ml-0 hidden'}`}>
                                Inflow
                            </span>
                        </Link>
                    </li>

                    <li className={" space-y-1.5"}>
                        <Link to={"/settings"}
                            style={{ width: isDrawerOpen ? 'auto' : '40px' }}
                            className={`transition-all duration-500 ease-in-out flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg 
                                ${currPath === "settings" ? theme.active : theme.text} 
                                ${theme.hover}`}>
                            <svg className="flex-shrink-0 mt-0.5 size-4" xmlns="http://www.w3.org/2000/svg"
                                width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="18" cy="15" r="3" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M10 15H6a4 4 0 0 0-4 4v2" />
                                <path d="m21.7 16.4-.9-.3" />
                                <path d="m15.2 13.9-.9-.3" />
                                <path d="m16.6 18.7.3-.9" />
                                <path d="m19.1 12.2.3-.9" />
                                <path d="m19.6 18.7-.4-1" />
                                <path d="m16.8 12.3-.4-1" />
                                <path d="m14.3 16.6 1-.4" />
                                <path d="m20.7 13.8 1-.4" />
                            </svg>
                            <span
                                className={` ${isDrawerOpen ? 'opacity-100 max-width-[200px] ml-2 ' : 'opacity-0 max-width-0 ml-0 hidden'}`}>Settings</span>

                        </Link>
                    </li>
                </ul>

                <ul className={"absolute bottom-4 space-y-2"}>
                    {isOwner  && (
                        <li>
                            <Link to={"/superadmin"}
                                style={{ width: isDrawerOpen ? 'auto' : '40px', overflowX: "hidden" }}
                                className="transition-all duration-500 ease-in-out flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg bg-gradient-to-r from-violet-500 to-indigo-500 text-white hover:from-violet-600 hover:to-indigo-600">
                                <svg className="flex-shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                <span className={`whitespace-nowrap ${isDrawerOpen ? 'opacity-100 ml-2' : 'opacity-0 hidden'}`}>
                                    Manage Academies
                                </span>
                            </Link>
                        </li>
                    )}

                    <li className={" space-y-1.5"}>
                        <span onClick={handleLogout}
                            style={{ width: isDrawerOpen ? 'auto' : '40px' }}
                            className={`transition-all duration-500 ease-in-out cursor-pointer inline-flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg 
                                ${theme.text} ${theme.hover}`}>
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M12.75 12L15.75 9M15.75 9L12.75 6M15.75 9L5.25 9M9.75 12V12.75C9.75 13.9926 8.74264 15 7.5 15H4.5C3.25736 15 2.25 13.9926 2.25 12.75V5.25C2.25 4.00736 3.25736 3 4.5 3H7.5C8.74264 3 9.75 4.00736 9.75 5.25V6"
                                    stroke="#FF0000"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round" />
                            </svg>
                            <span
                                className={` ${isDrawerOpen ? 'opacity-100 max-width-[200px] ml-2 block text-red-500 font-semibold' : 'opacity-0 max-width-0 ml-0 hidden'}`}>Logout</span>
                        </span>
                    </li>
                </ul>
            </nav>
        </div>

        <div className={`bg-[#FAFAFB] w-full overflow-y-auto h-[100%] pt-16 lg:pt-16
            ${hasRoles ? 'bg-gradient-to-r from-purple-50/30 to-white' : ''}`}>
            <ErrorBoundary FallbackComponent={ErrorFallbackPage}>
                <div className="h-[100%] mt-0 lg:mt-[20px] ml-0 lg:ml-[80px] p-2 sm:p-4 space-y-2 sm:space-y-4">
                    <Outlet />
                </div>
            </ErrorBoundary>
        </div>


    </div>)

}